import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { notifications } from '@mantine/notifications'
import {
  type TemplateCreateInput,
  useTemplateCreate,
} from '../../hooks/useTemplates'
import { extractApiError } from '../../lib/extractApiError'
import styles from '../_shared/CrudPage.module.css'
import formStyles from '../_shared/CrudForm.module.css'

const TIPOS_OS = [
  { value: 'preventivo', label: 'Preventivo' },
  { value: 'corretivo', label: 'Corretivo' },
  { value: 'higienizacao', label: 'Higienização' },
  { value: 'qai', label: 'Análise QAI' },
  { value: 'desinfeccao_legionella', label: 'Desinfecção — Legionella' },
  { value: 'emergencial', label: 'Emergencial' },
]

const TIPOS_EQUIPAMENTO = [
  { value: '', label: 'Qualquer tipo (genérico)' },
  { value: 'split_hi_wall', label: 'Split Hi-Wall' },
  { value: 'split_cassete', label: 'Split Cassete' },
  { value: 'split_piso_teto', label: 'Split Piso-Teto' },
  { value: 'split_duto', label: 'Split Dutado' },
  { value: 'vrf_condensadora', label: 'VRF Condensadora' },
  { value: 'vrf_evaporadora', label: 'VRF Evaporadora' },
  { value: 'chiller', label: 'Chiller' },
  { value: 'self_contained', label: 'Self-Contained' },
  { value: 'uta', label: 'UTA' },
  { value: 'fancoil', label: 'Fan Coil' },
  { value: 'torre_resfriamento', label: 'Torre de Resfriamento' },
  { value: 'condensadora_ar', label: 'Condensadora a Ar' },
  { value: 'janela', label: 'Ar de Janela' },
  { value: 'ptac', label: 'PTAC' },
]

export default function TemplateNovoPage() {
  const navigate = useNavigate()
  const create = useTemplateCreate()
  const [form, setForm] = useState<TemplateCreateInput>({
    nome: '',
    tipo_os: 'preventivo',
    tipo_equipamento: '',
    descricao: '',
    ativo: true,
  })

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.nome.trim()) {
      notifications.show({ color: 'red', message: 'Nome é obrigatório.' })
      return
    }
    try {
      const novo = await create.mutateAsync(form)
      notifications.show({
        color: 'green',
        message: `Template "${novo.nome}" criado. Adicione itens agora.`,
      })
      navigate(`/checklists/templates/${novo.id}`)
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Falha',
        message: extractApiError(err, 'Erro ao criar template.'),
      })
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <button
            type="button"
            className={styles.backBtn}
            onClick={() => navigate('/checklists/templates')}
          >
            ← Voltar
          </button>
          <h1 className={styles.title}>Novo template de checklist</h1>
        </div>
      </header>

      <form onSubmit={handleSubmit} className={formStyles.form}>
        <section className={formStyles.section}>
          <h2 className={formStyles.sectionTitle}>Identificação</h2>
          <div className={formStyles.grid2}>
            <div className={`${formStyles.field} ${formStyles.span2}`}>
              <label htmlFor="nome">Nome *</label>
              <input
                id="nome"
                data-testid="template-nome"
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                required
                placeholder="ex.: Preventivo mensal — Split Hi-Wall"
              />
            </div>

            <div className={formStyles.field}>
              <label htmlFor="tipo_os">Tipo de OS *</label>
              <select
                id="tipo_os"
                data-testid="template-tipo-os"
                value={form.tipo_os}
                onChange={(e) => setForm({ ...form, tipo_os: e.target.value })}
                required
              >
                {TIPOS_OS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>

            <div className={formStyles.field}>
              <label htmlFor="tipo_equipamento">Tipo de equipamento</label>
              <select
                id="tipo_equipamento"
                data-testid="template-tipo-equipamento"
                value={form.tipo_equipamento ?? ''}
                onChange={(e) =>
                  setForm({ ...form, tipo_equipamento: e.target.value })
                }
              >
                {TIPOS_EQUIPAMENTO.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
              <small style={{ color: '#666' }}>
                Em branco = template genérico, aplica-se a qualquer equipamento.
              </small>
            </div>

            <div className={`${formStyles.field} ${formStyles.span2}`}>
              <label htmlFor="descricao">Descrição</label>
              <textarea
                id="descricao"
                data-testid="template-descricao"
                value={form.descricao ?? ''}
                onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                rows={3}
                placeholder="Breve descrição do escopo do template (opcional)"
              />
            </div>
          </div>
        </section>

        <div className={formStyles.actions}>
          <button
            type="submit"
            className={formStyles.btnPrimary}
            disabled={create.isPending}
            data-testid="template-submit"
          >
            {create.isPending ? 'Criando...' : 'Criar template e continuar'}
          </button>
        </div>
      </form>
    </div>
  )
}
